import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { ZitaLogo, ZitaWordmark } from '@/components/ZitaLogo';
import { PublicFooter } from '@/components/PublicFooter';
import { HoneypotFields, useHoneypot } from '@/components/Honeypot';
import { PasswordInput } from '@/components/PasswordInput';
import { AUTH } from '@/constants/testIds';
import { toast } from 'sonner';

const ResetPassword = () => {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const honeypot = useHoneypot();

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) {
      toast.error('Password must be at least 8 characters.');
      return;
    }
    if (pw !== pw2) {
      toast.error('Passwords do not match.');
      return;
    }
    setBusy(true);
    try {
      await api.post('/auth/reset-password', {
        token,
        new_password: pw,
        ...honeypot.getValues(),
      });
      toast.success('Password reset. Please sign in.');
      navigate('/login');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reset failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <main className="flex-1 flex flex-col items-center justify-center px-4 py-10">
        <Link to="/" className="flex items-center gap-3 mb-8">
          <ZitaLogo size={44} />
          <ZitaWordmark className="text-3xl" />
        </Link>
        <form onSubmit={submit} className="zita-card p-8 w-full max-w-md space-y-4">
          <h1 className="font-serif text-3xl text-zita-espresso">Set a new password</h1>
          <HoneypotFields inputRef={honeypot.websiteRef} />
          <div>
            <label className="block text-xs uppercase tracking-wider text-zita-muted mb-1">
              New password
            </label>
            <PasswordInput
              required
              minLength={8}
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              testId={AUTH.resetPassword}
              autoComplete="new-password"
              placeholder="At least 8 characters"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-zita-muted mb-1">
              Confirm password
            </label>
            <PasswordInput
              required
              minLength={8}
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              testId="auth-reset-password-confirm"
              autoComplete="new-password"
              placeholder="Re-enter your new password"
            />
          </div>
          <button
            type="submit"
            disabled={busy}
            data-testid={AUTH.resetSubmit}
            className="zita-btn-primary w-full"
          >
            {busy ? 'Saving…' : 'Set password'}
          </button>
          <div className="text-center">
            <Link to="/login" className="text-sm text-zita-cocoa hover:underline">Back to sign in</Link>
          </div>
        </form>
      </main>
      <PublicFooter />
    </div>
  );
};

export default ResetPassword;
